import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/api-client";
import { Dialog, PrimaryButton, QuietButton } from "./controls";

type Result = { warning?: string; [key: string]: unknown };
type Pending = {
  title: string; message: string; confirmationLabel: string; bypassField: "allowConflict" | "allowOutsideRegularHours";
  path: string; method: string; body: object; resolve: (result: Result) => void; reject: (error: Error) => void;
};

/** Office scheduling can acknowledge conflicts without silently bypassing them. */
export function useOfficeSchedulingSave() {
  const [pending, setPending] = useState<Pending | null>(null);
  const active = useRef<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => () => { active.current?.reject(new Error("Scheduling closed.")); active.current = null; }, []);
  const request = async (path: string, method: string, body: object): Promise<Result> => {
    const response = await apiFetch(path, { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const payload = await response.json();
    if (!response.ok) throw Object.assign(new Error(payload.error?.message || "Appointment could not be saved."), { code: payload.error?.code });
    window.dispatchEvent(new Event("workview:alerts-changed"));
    return payload.data ?? {};
  };
  const acknowledgement = (cause: Error, path: string, method: string, body: object, resolve: (result: Result) => void, reject: (error: Error) => void): Pending | null => {
    const code = (cause as Error & { code?: string }).code;
    if (code !== "SCHEDULING_CONFLICT" && code !== "OUTSIDE_REGULAR_HOURS_CONFIRMATION") return null;
    const outsideRegularHours = code === "OUTSIDE_REGULAR_HOURS_CONFIRMATION";
    return {
      title: outsideRegularHours ? "Outside regular hours" : "Scheduling conflict",
      message: cause.message,
      confirmationLabel: outsideRegularHours ? "Continue anyway" : "Book anyway",
      bypassField: outsideRegularHours ? "allowOutsideRegularHours" : "allowConflict",
      path, method, body, resolve, reject,
    };
  };
  const save = async (path: string, method: string, body: object): Promise<Result> => {
    try { return await request(path, method, body); }
    catch (cause) {
      if (!(cause instanceof Error)) throw cause;
      const next = acknowledgement(cause, path, method, body, () => {}, () => {});
      if (!next) throw cause;
      return new Promise<Result>((resolve, reject) => {
        const pending = acknowledgement(cause, path, method, body, resolve, reject)!;
        active.current = pending; setError(""); setPending(pending);
      });
    }
  };
  const cancel = () => {
    if (busy) return;
    active.current?.reject(new Error("Booking cancelled. Choose another time or technician."));
    active.current = null; setPending(null);
  };
  const confirm = async () => {
    if (!pending || busy) return;
    setBusy(true); setError("");
    try {
      const body = { ...pending.body, [pending.bypassField]: true };
      const result = await request(pending.path, pending.method, body);
      pending.resolve({ ...result, warning: [pending.message, result.warning].filter(Boolean).join(" · ") });
      active.current = null; setPending(null);
    } catch (cause) {
      if (cause instanceof Error) {
        const next = acknowledgement(cause, pending.path, pending.method, { ...pending.body, [pending.bypassField]: true }, pending.resolve, pending.reject);
        if (next) { active.current = next; setPending(next); return; }
      }
      setError(cause instanceof Error ? cause.message : "Booking could not be saved. Try again.");
    }
    finally { setBusy(false); }
  };
  const conflictDialog = pending && <Dialog title={pending.title} onClose={cancel}>
    <p className="text-sm">{pending.message}</p>
    <p className="mt-2 text-sm text-[var(--ink-soft)]">You can continue with this booking despite the warning.</p>
    {error && <p role="alert" className="mt-3 text-sm text-[var(--accent)]">{error}</p>}
    <div className="mt-4 flex gap-2"><PrimaryButton type="button" disabled={busy} onClick={() => void confirm()}>{busy ? "Saving…" : pending.confirmationLabel}</PrimaryButton><QuietButton type="button" disabled={busy} onClick={cancel}>Cancel</QuietButton></div>
  </Dialog>;
  return { save, conflictDialog };
}
